/* eslint-disable arrow-body-style */
import type { IDynamicFormControlValueAccessor } from '@gp/components/DynamicForm/components/FormControl/types';
import type { IRecord } from '@gp/types/global';
import { formatDateStr } from '@gp/utils/common';
import RangePicker from '@home/components/RangePicker';
import type { IBaseProps, INullableMoment } from '@home/types/common';
import { componentDefault } from '@intelligence/components/Filter/FilterBase/FilterList';
import classnames from 'classnames';
import moment from 'moment';
import React, { useMemo } from 'react';

import './index.scss';

export type IDateType = 'date' | 'week' | 'month' | (string & {});

export interface IGPRangePickerValue {
  dateType: IDateType;
  startDate: string;
  endDate: string;
}

export interface IGPRangePickerProps
  extends IBaseProps,
    IDynamicFormControlValueAccessor<IGPRangePickerValue> {
  // RangePicker组件的button
  label?: React.ReactElement | null;
  // 符合RangePicker组件的禁用日期数据
  disabledDate?: IRecord<(m?: moment.Moment) => boolean>;
  disabledMonth?: (m?: moment.Moment) => boolean;
  showSelectType?: boolean;
  showShortcut?: boolean;
  syncInput?: boolean;
  isNeedDiff?: boolean;
  autoRange?: {
    timeType: IDateType;
    length: number;
  };
  startOfWeek?: number;
  startOfTime?: string;
  type: IDateType;
  typeList: IDateType[];
}

const LANGUAGE = 'en';
export const DATE_TYPE = 'date';
const DATE_TYPES = [DATE_TYPE];
export const COMMON_DATE_TYPES = [DATE_TYPE, 'week', 'month'];
// const AUTO_RANGE = {
//   timeType: DATE_TYPE,
//   length: 0,
// };

const GPRangePicker: React.FC<IGPRangePickerProps> = (props) => {
  const {
    style,
    className,
    label,
    disabledDate,
    disabledMonth,
    showSelectType = false,
    showShortcut = false,
    syncInput = false,
    autoRange,
    startOfWeek = 1,
    startOfTime,
    // startOfTime: minDate,
    endOfTime,
    type: typeProps = DATE_TYPE,
    typeList = DATE_TYPES,
    value: valueProps,
    onChange: onChangeProps,
    isNeedDiff = false,
    isSingleSelect = false,
    disabled = false,
  } = props;

  // const [shortcutType, setShortcutType] = useState('');
  // const [isAllOfTime, setIsAllOfTime] = useState(false);

  // 符合RangePicker组件的value
  const value = useMemo<[moment.Moment, moment.Moment] | ['', '']>(() => {
    const { startDate, endDate } = valueProps || {};
    if (!typeList?.includes(typeProps) || !startDate || !endDate) {
      return ['', ''];
    }
    return [moment(startDate), moment(endDate)];
  }, [valueProps, typeProps, typeList]);

  // 符合RangePicker组件的type
  const type = useMemo<string>(() => {
    if (typeList?.includes(typeProps)) {
      return typeProps;
    }
    return Array.isArray(typeList) && typeList?.length > 0 ? typeList[0] : DATE_TYPE;
  }, [typeProps, typeList]);

  // RangePicker组件的change回调
  const handleChange = (value: any, type: string) => {
    let dateValue = ['', ''];
    // setShortcutType('');
    if (Array.isArray(value) && value.length === 2) {
      const startDate = value[0];
      let endDate = value[1];
      if (startDate === '' && endDate === '') {
        dateValue = componentDefault?.[type];
      } else {
        if (type === 'year') {
          endDate = moment(endDate).endOf('year');
        }
        if (type === 'month') {
          endDate = moment(endDate).endOf('month');
        }
        dateValue = [startDate, endDate];
      }
    }

    onChangeProps?.({
      dateType: type,
      startDate: formatDateStr(dateValue?.[0]),
      endDate: formatDateStr(dateValue?.[1]),
    });
  };

  // 切换时间快捷键
  // const changeShortcutType = (value: string) => {
  //   setShortcutType(value);
  // };

  const isAllOfTime = useMemo<string>(() => {
    const typeTemp: any = type || '';
    if (!Array.isArray(value) || value?.length < 2 || !typeTemp) return false;
    const minDateM: INullableMoment = !startOfTime ? null : moment(startOfTime).startOf(typeTemp);
    const maxDateM: INullableMoment = !endOfTime ? null : moment(endOfTime).endOf(typeTemp);
    return (
      moment(value[0]).isSameOrBefore(minDateM, typeTemp) &&
      moment(value[1]).isSameOrAfter(maxDateM, typeTemp)
    );
  }, [value, type, startOfTime, endOfTime]);

  const diffClassName = useMemo(() => {
    // if (isNeedDiff && shortcutType === 'All of time') return '';
    if (isNeedDiff && !isAllOfTime) return 'dbg-diff-filter-value';
    return '';
  }, [isNeedDiff, isAllOfTime]);

  return (
    <div
      style={style}
      className={classnames(
        'dbg-range-picker',
        className,
        { 'has-type-select': showSelectType },
        diffClassName,
      )}
    >
      <RangePicker
        className="search-border"
        language={LANGUAGE}
        showSelectType={showSelectType}
        showShortcut={showShortcut}
        syncInput={syncInput}
        autoRange={autoRange}
        startOfWeek={startOfWeek}
        startOfTime={startOfTime}
        type={type}
        typeList={typeList}
        disabledDate={disabledDate}
        disabledMonth={disabledMonth}
        label={label}
        defaultValue={componentDefault}
        value={value}
        onChange={handleChange}
        isSingleSelect={isSingleSelect}
        disabled={disabled}
        // setTimeShortcutType={changeShortcutType}
      />
    </div>
  );
};

export default GPRangePicker;
